import { DocumentOptions } from '@/features/section-2/application/domain/entities/DocumentOptions';
import {
  CompleterReviewDto,
  IRemoteI9Repository,
} from '@/features/remote_i9/application/ports/outbound/IRemoteI9Repository';
import { InitiateRemoteI9RequestDto } from '@/features/remote_i9/infra/repositories/dto/req/InitiateRemoteI9RequestDto';
import { InitiateRemoteI9ResponseDto } from '@/features/remote_i9/infra/repositories/dto/res/InitiateRemoteI9ResponseDto';
import Section2, {
  Section1Summary,
  DynamicFieldSchema,
  DocumentList,
} from '@/features/section-2/application/domain/entities/Section2';
import CustomDocumentType from '@/features/section-2/application/domain/entities/CustomDocumentType';
import { Section2Steps } from '@/features/section-2/application/domain/models/section-2-steps';
import { Section2Form } from '@/features/section-2/application/validators/section-2.schema';
import { FileUploadResponse } from '@/features/section-2/infra/repositories/ApiSection2Repository';
import Section2ResponseDto, {
  Section2Response,
} from '@/features/section-2/infra/repositories/dto/res/Section2ResponseDto';
import {
  mapSection2FormToDto,
  mapSection2MutationResponseDtoToEntity,
  mapSection2QueryResposeDtoToEntity,
  mapSectionOneDtoToSummary,
} from '@/features/section-2/infra/repositories/mappers/section2Mapper';
import { HttpClient } from '@/shared';
import { RemoteI9AuthResponseDto } from '@/features/remote_i9/infra/repositories/dto/res/RemoteI9AuthResponseDto';
import { SectionOneResponseDto } from '@/features/section-1/infra/repositories/dto/res/SectionOneResponseDto';
import { CustomDocumentTypeDto } from '@/features/section-2/infra/repositories/dto/res/CustomDocumentTypeResponseDto';
import { mapCustomDocumentTypeDtoToEntity } from '@/features/section-2/infra/repositories/mappers/customDocumentTypeMapper';
import { BlobDocumentResponseDto } from '@/features/section-2/infra/repositories/dto/res/BlobDocumentResponseDto';
import { DocumentTypesResponseDto } from '@/features/section-2/infra/repositories/dto/res/DocumentTypesResponseDto';
import { mapDocumentTypesDtoToEntity } from '@/features/section-2/infra/repositories/mappers/documentTypesMapper';
import { mapDocumentSchemaDtoToEntity } from '@/features/section-2/infra/repositories/mappers/documentSchemaMapper';
import { DocumentSchemaDataDto } from '@/features/section-2/infra/repositories/dto/res/DocumentSchemaResponseDto';
import type { DocumentFileUpload, DocumentUploads } from '@/features/section-2/application/domain/entities/DocumentUploads';

export class ApiRemoteI9Repository implements IRemoteI9Repository {
  constructor(private httpClient: HttpClient) {}

  // ─── Remote I-9 Specific ──────────────────────────────────────────────────

  async authenticateToken(token: string): Promise<RemoteI9AuthResponseDto> {
    const response = await this.httpClient.post<RemoteI9AuthResponseDto>(
      `/remote-i9/auth/${token}`
    );
    return response.data;
  }

  async fetchCompleterForReview(token: string): Promise<CompleterReviewDto> {
    const response = await this.httpClient.get<CompleterReviewDto>('/remote-i9/completer-review', {
      params: { token },
    });
    return response.data;
  }

  async updateCompleterFromReview(
    token: string,
    completer: { firstName: string; lastName: string; email: string }
  ): Promise<void> {
    await this.httpClient.patch('/remote-i9/completer-review', { token, ...completer });
  }

  async initiateRemoteI9(
    payload: InitiateRemoteI9RequestDto
  ): Promise<InitiateRemoteI9ResponseDto> {
    const response = await this.httpClient.post<InitiateRemoteI9ResponseDto>('/remote-i9', payload);
    return response.data;
  }

  // ─── Section 2 Overrides ──────────────────────────────────────────────────

  async fetchSection1Summary(): Promise<Section1Summary> {
    const sectionOne = await this.httpClient.get<SectionOneResponseDto>(`/remote-i9/section1`, {
      params: {
        isEncrypted: false,
      },
    });

    return mapSectionOneDtoToSummary(sectionOne.data);
  }

  async getSection2(): Promise<Section2> {
    const response = await this.httpClient.get<Section2Response>(`/remote-i9/section2`);
    return mapSection2QueryResposeDtoToEntity(response.data);
  }

  async fetchAlternativeProcedureAllowed(): Promise<boolean> {
    const response = await this.httpClient.get<{ alternativeProcedureAllowed: boolean }>(
      '/remote-i9/section2/alternative-procedure'
    );
    return response.data.alternativeProcedureAllowed === true;
  }

  async upsertSection2(formValues: Section2Form, currentStep: Section2Steps): Promise<Section2> {
    const requestDto = mapSection2FormToDto(formValues, currentStep, '');
    // Remote I-9 uses a different endpoint than the standard section2
    const response = await this.httpClient.patch<Section2ResponseDto>(
      `/remote-i9/section2`,
      requestDto
    );
    return mapSection2MutationResponseDtoToEntity(response.data);
  }

  async fetchDocumentSchema(
    documentSelection: string,
    _i9FormId?: string,
    opts?: { list?: 'A' | 'B' | 'C'; documentTypeCode?: string }
  ): Promise<DynamicFieldSchema[]> {
    const response = await this.httpClient.get<DocumentSchemaDataDto>(
      '/remote-i9/documents/schema',
      {
        params: {
          docType: documentSelection,
          list: opts?.list,
          documentTypeCode: opts?.documentTypeCode,
        },
      }
    );
    return mapDocumentSchemaDtoToEntity(response.data.fields);
  }

  async fetchDocumentFiles(_i9FormId?: string): Promise<DocumentUploads> {
    const response = await this.httpClient.get<DocumentUploads>('/remote-i9/document-files');
    return response.data;
  }

  async recordDocumentFile(
    _i9FormId: string | undefined,
    upload: DocumentFileUpload
  ): Promise<DocumentUploads> {
    const response = await this.httpClient.post<DocumentUploads>('/remote-i9/document-files', upload);
    return response.data;
  }

  async fetchDocumentOptions(_i9FormId?: string, statusCode?: string): Promise<DocumentOptions> {
    const response = await this.httpClient.get<DocumentOptions>('/remote-i9/documents/options', {
      params: { statusCode },
    });
    return response.data;
  }

  async fetchDocumentTypes(): Promise<DocumentList[]> {
    const response = await this.httpClient.get<DocumentTypesResponseDto>(
      '/remote-i9/documents/types'
    );
    return mapDocumentTypesDtoToEntity(response.data);
  }

  async uploadFile(file: File): Promise<{ url: string; hash: string }> {
    const formData = new FormData();
    formData.append('file', file, file.name);

    const response = await this.httpClient.post<FileUploadResponse>('/remote-i9/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async downloadDocument(documentUrl: string): Promise<string> {
    const response = await this.httpClient.get<BlobDocumentResponseDto>('/remote-i9/document', {
      params: {
        url: documentUrl,
      },
    });

    return response.data.document;
  }

  async fetchDistinctDocumentTypes(): Promise<CustomDocumentType[]> {
    const response = await this.httpClient.get<CustomDocumentTypeDto[]>(
      '/remote-i9/documents/distinct-types'
    );
    return response.data.map(mapCustomDocumentTypeDtoToEntity);
  }
}
